/** How many seconds before a track ends to warm the next track's audio. */
export const PRELOAD_LEAD_SECONDS = 12;

export interface PreloadDecision {
  /** Whether the next track should be warmed now. */
  shouldPreload: boolean;
  /** Index of the track that plays after the current one (wraps the loop). */
  nextIndex: number;
}

/** The deterministic next index in a looping station playlist. */
export function nextStationIndex(currentIndex: number, length: number): number {
  if (length <= 0) return 0;
  return (currentIndex + 1) % length;
}

/**
 * Decide whether to warm the upcoming track. Preloading shrinks the gap at the
 * boundary by letting the browser fetch the next file (served from CDN cache)
 * before the current one ends. No-ops for single-track loops where "next" is the
 * same file already playing.
 */
export function evaluatePreload(
  currentIndex: number,
  currentTimeSeconds: number,
  durationSeconds: number,
  playlistLength: number,
  lead: number = PRELOAD_LEAD_SECONDS,
): PreloadDecision {
  const nextIndex = nextStationIndex(currentIndex, playlistLength);
  if (playlistLength <= 1 || durationSeconds <= 0) {
    return { shouldPreload: false, nextIndex };
  }
  const remaining = durationSeconds - currentTimeSeconds;
  return { shouldPreload: remaining > 0 && remaining <= lead, nextIndex };
}
